import React, { useCallback, useMemo, useState } from 'react';
import { Box, ButtonBase, makeStyles } from '@material-ui/core';
import _ from 'lodash';
import { FemaleIcon, MaleIcon, MoreIcon } from '../../../../../components/Icon';
import { ReactComponent as Xmale } from '../../../../../static/images/Xmale.svg';
import { BED_STATUS_CUBICLE_COLOR, BED_STATUS_REMARK_COLOR } from '../../../../../comm/constant';
import { classNames, onKeyUpEnterOrSpace } from '../../../../../utils';
import useCheckLogin from '../../../../../utils/useCheckLogin';
import LocCubicleDialog from '../../LocCubicleDialog';
import EADBed from './EADBed';
import LandingUpdateCubicleDialog from './LandingUpdateCubicleDialog';

const useStyles = makeStyles(() => ({
  root: ({ color, borderLine, hasNoBeds }) => ({
    // 没有床位时撑满 floorplan 保存的 cubicle 宽高；有床位时保持原有按内容布局。
    ...(hasNoBeds && {
      width: '100%',
      height: '100%',
      boxSizing: 'border-box',
    }),
    padding: '0.571rem 0.813rem 0.571rem 0.688rem',
    borderRadius: '8px',
    backgroundColor: BED_STATUS_CUBICLE_COLOR[color] ?? '#fff',
    display: 'inline-flex',
    flexDirection: 'column',
    // minWidth: '11rem',
    minHeight: '9.3rem',
    border: borderLine === 'show' ? 'solid 1px #AAAAAA' : 'unset',
  }),
  maleIcon: {
    fontSize: '1rem',
    color: '#538fff',
    marginRight: '0.25rem',
  },
  femaleIcon: {
    fontSize: '1rem',
    color: '#ff8181',
    marginRight: '0.25rem',
  },
  xmaleIcon: {
    fontSize: '1rem',
    color: '#a375ff',
  },
  title: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '0.875rem',
    color: '#2e4469',
    lineHeight: '1.05rem',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  subTitle: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.75rem',
    color: '#2e4469',
  },
  status: {
    backgroundColor: '#979bff',
    padding: '0.125rem 0.25rem',
    borderRadius: '2px',
  },
  statusContent: {
    color: '#fff',
    fontSize: '0.75rem',
    fontFamily: 'NotoSans-Medium',
  },
  tipBarBox: () => ({
    margin: '0.5rem 0',
    height: '2rem',
  }),
  tipBar: ({ remarkColor }) => ({
    backgroundColor: BED_STATUS_REMARK_COLOR[remarkColor]?.bgColor ?? '#fff',
    borderRadius: '4px',
    border: 'solid 1px #e4e7eb',
    padding: '0.25rem 0.5rem',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    overflow: 'hidden',
    '&::before': {
      position: 'absolute',
      top: '0',
      left: '0',
      content: '""',
      display: 'block',
      width: '0.25rem',
      height: '100%',
      backgroundColor: BED_STATUS_REMARK_COLOR[remarkColor]?.leftColor ?? '#fff',
      zIndex: '9',
    },
  }),
  tips: ({ remarkColor }) => ({
    fontFamily: 'NotoSans-Medium',
    color: BED_STATUS_REMARK_COLOR[remarkColor]?.color ?? '#2e4469',
    fontSize: '0.75rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: 'normal',
  }),
  border: () => ({
    width: '100%',
    height: '0.4rem',
    backgroundColor: 'unset',
    margin: '0',
  }),
  popupContent: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1.25rem',
    color: '#2e4469',
  },
  clickStyle: {
    '&:hover': {
      cursor: 'default',
    },
  },
  moreButton: ({ color }) => ({
    position: 'absolute',
    top: '0.15rem',
    right: '0.15rem',
    width: '1.714rem',
    height: '1.714rem',
    border: 'solid 1px #e4e7eb',
    borderRadius: '4px',
    backgroundColor: BED_STATUS_CUBICLE_COLOR[color] ?? '#fff',
    zIndex: 1,
  }),
  moreIcon: {
    fontSize: '0.875rem',
    color: '#7d86a9',
  },
}));

const EADCubicle = ({
  style = {},
  cubicleData,
  id,
  handleUpdateBedNo,
  handleUpdateCubicle = null,
  handleDeleteCubicle = null,
  handleCopyCubicle = null,
  selectedBed = null,
  handleSelectBed = null,
  handleDeleteBedPatient = null,
  handleOpenPatientDetail = null,
  editMode = false,
  viewType = '',
  showDetails = true,
  patientInfoViewType = '',
}) => {
  const hasNoBeds = !Array.isArray(cubicleData?.beds) || cubicleData.beds.length === 0;
  const classes = useStyles({
    color: cubicleData?.color,
    remarkColor: cubicleData?.remarkColor,
    borderLine: cubicleData?.borderLine,
    hasNoBeds,
  });
  const [editOpen, setEditOpen] = useState(false);
  const checkLogin = useCheckLogin();
  const showHeader = useMemo(() => cubicleData?.header === 'show', [cubicleData?.header]);

  const handleOpen = useCallback(() => {
    if (checkLogin()) {
      setEditOpen(true);
    }
  }, [checkLogin, cubicleData]);

  const renderEditPopup = () => {
    const params = {
      handleClose: () => setEditOpen(false),
      showPopup: editOpen,
      cubicleId: id,
      cubicleData: cubicleData,
      handleSubmit: handleUpdateCubicle,
      viewType: viewType,
    };

    if (editMode) {
      return (
        <LocCubicleDialog
          {...params}
          operation="edit"
          handleCopyCubicle={handleCopyCubicle}
          handleDeleteCubicle={handleDeleteCubicle}
        />
      );
    }

    return <LandingUpdateCubicleDialog {...params} />;
  };

  return (
    <>
      <div className={classes.root} style={style}>
        {/* 没有header时保留一个打开编辑弹窗入口。 */}
        {!showHeader && (
          <ButtonBase className={classes.moreButton} onClick={handleOpen}>
            <MoreIcon className={classes.moreIcon} />
          </ButtonBase>
        )}
        {showHeader && (
          <>
            <Box display="flex" alignItems="flex-start" justifyContent="space-between">
              <Box display="flex" flexDirection="column">
                <Box display="flex" alignItems="center">
                  <ButtonBase onClick={handleOpen}>
                    {cubicleData?.gender === 'M' && <MaleIcon className={classes.maleIcon} />}
                    {cubicleData?.gender === 'F' && <FemaleIcon className={classes.femaleIcon} />}
                    {cubicleData?.gender === 'X' && <Xmale className={classes.xmaleIcon} />}
                  </ButtonBase>
                  <div
                    className={classNames(classes.title, classes.clickStyle)}
                    onClick={handleOpen}
                    tabIndex={0}
                    onKeyUp={(e) => onKeyUpEnterOrSpace(e, handleOpen)}
                  >
                    {cubicleData?.name}
                  </div>
                </Box>
                {cubicleData?.type && (
                  <Box display="flex" alignItems="center" mt="0.142rem" height="0.9rem">
                    <div
                      className={classNames(classes.subTitle, classes.clickStyle)}
                      onClick={handleOpen}
                      tabIndex={0}
                      onKeyUp={(e) => onKeyUpEnterOrSpace(e, handleOpen)}
                    >
                      {cubicleData?.type}
                    </div>
                  </Box>
                )}
              </Box>
              {cubicleData?.status && (
                <Box display="flex" alignItems="center">
                  <div
                    className={classNames(classes.status, classes.clickStyle)}
                    onClick={handleOpen}
                    tabIndex={0}
                    onKeyUp={(e) => onKeyUpEnterOrSpace(e, handleOpen)}
                  >
                    <div className={classes.statusContent}>{cubicleData?.status}</div>
                  </div>
                </Box>
              )}
            </Box>
            {cubicleData?.remarks ? (
              <Box className={classes.tipBarBox}>
                <Box position="relative">
                  <div
                    className={classNames(classes.tipBar, classes.clickStyle)}
                    onClick={handleOpen}
                    tabIndex={0}
                    onKeyUp={(e) => onKeyUpEnterOrSpace(e, handleOpen)}
                  >
                    <div className={classes.tips} title={cubicleData?.remarks}>
                      {cubicleData?.remarks}
                    </div>
                  </div>
                </Box>
              </Box>
            ) : (
              <div className={classes.border} />
            )}
          </>
        )}
        {
          <Box
            display="grid"
            gridTemplateRows={`repeat(${cubicleData?.bedArrangement?.row}, 1fr)`}
            gridTemplateColumns={`repeat(${cubicleData?.bedArrangement?.column}, 1fr)`}
            gridGap="0.2rem"
          >
            {_.map(cubicleData?.beds, (item) => (
              <EADBed
                key={item.key}
                bedData={item}
                infectionIcons={item.infectionIcons}
                editMode={editMode}
                handleUpdateBedNo={editMode ? handleUpdateBedNo : undefined}
                selectedBed={selectedBed}
                cubicleId={id}
                cubicleVersion={cubicleData?.version}
                handleSelectBed={handleSelectBed}
                handleDeleteBedPatient={handleDeleteBedPatient}
                handleOpenPatientDetail={handleOpenPatientDetail}
                viewType={viewType}
                showDetails={showDetails}
                patientInfoViewType={patientInfoViewType}
              />
            ))}
          </Box>
        }
      </div>

      {editOpen && renderEditPopup()}
    </>
  );
};

export default EADCubicle;
